import * as React from 'react';

import { useAtom } from 'jotai';

import { $columnOrders } from './columnOrder.state';

const mergeColumnOrder = (persisted: string[], current: string[]): string[] => {
  const currentSet = new Set(current);
  const validPersisted = persisted.filter((col) => currentSet.has(col));
  const persistedSet = new Set(validPersisted);
  const newColumns = current.filter((col) => !persistedSet.has(col));
  return [...validPersisted, ...newColumns];
};

type ColumnOrderUpdater = string[] | ((prev: string[]) => string[]);

type ColumnOrderResult = {
  columnOrder: string[];
  setColumnOrder: (updaterOrValue: ColumnOrderUpdater) => void;
};

export const useColumnOrder = (
  tableKey: string | undefined,
  defaultOrder: string[],
): ColumnOrderResult => {
  const [allOrders, setAllOrders] = useAtom($columnOrders);

  const columnOrder = React.useMemo(() => {
    if (!tableKey) return defaultOrder;

    const persisted = allOrders[tableKey];

    if (!persisted) return defaultOrder;

    return mergeColumnOrder(persisted, defaultOrder);
  }, [tableKey, allOrders, defaultOrder]);

  const setColumnOrder = React.useCallback(
    (updaterOrValue: ColumnOrderUpdater) => {
      if (!tableKey) return;

      setAllOrders((prev) => {
        const persistedOrder = prev[tableKey];
        const currentMergedOrder = persistedOrder
          ? mergeColumnOrder(persistedOrder, defaultOrder)
          : defaultOrder;

        const newOrder =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(currentMergedOrder)
            : updaterOrValue;

        return {
          ...prev,
          [tableKey]: newOrder,
        };
      });
    },
    [tableKey, setAllOrders, defaultOrder],
  );

  return { columnOrder, setColumnOrder };
};
